(() => {
  const MIN_CM = 10;
  const MAX_CM = 120;
  const STITCH_CM = 0.6;
  const SAFE_MARGIN_CM = 1.0;
  const EXPORT_DPI = 150;

  function initBarmat(root) {
    if (!root || root.dataset.bmcReady === '1') return;
    root.dataset.bmcReady = '1';

    const q = s => root.querySelector(s);
    const el = {
      width:q('[data-bmc-width]'),
      height:q('[data-bmc-height]'),
      backgroundColor:q('[data-bmc-background-color]'),
      files:q('[data-bmc-files]'),
      text:q('[data-bmc-text]'),
      font:q('[data-bmc-font-size]'),
      fontFamily:q('[data-bmc-font-family]'),
      textColor:q('[data-bmc-text-color]'),
      addText:q('[data-bmc-add-text]'),
      scaleDown:q('[data-bmc-scale-down]'),
      scaleUp:q('[data-bmc-scale-up]'),
      layerUp:q('[data-bmc-layer-up]'),
      layerDown:q('[data-bmc-layer-down]'),
      del:q('[data-bmc-delete]'),
      layers:q('[data-bmc-layers]'),
      price:q('[data-bmc-price]'),
      quantity:q('[data-bmc-quantity]'),
      addCart:q('[data-bmc-add-cart]'),
      shopifyForm:q('[data-bmc-shopify-form]'),
      formQuantity:q('[data-bmc-form-quantity]'),
      propWidth:q('[data-bmc-prop-width]'),
      propHeight:q('[data-bmc-prop-height]'),
      propBackground:q('[data-bmc-prop-background]'),
      propPrice:q('[data-bmc-prop-price]'),
      designFileHolder:q('[data-bmc-design-file-holder]'),
      rx:q('[data-bmc-ruler-x]'),
      ry:q('[data-bmc-ruler-y]'),
      status:q('[data-bmc-status]'),
      canvasEl:q('[data-bmc-canvas]'),
      canvasBorder:q('[data-bmc-canvas-border]')
    };

    if (!el.canvasEl || !window.fabric) return;

    fabric.Object.prototype.set({
      transparentCorners:false,
      cornerSize:10,
      touchCornerSize:20,
      padding:2
    });

    const canvas = new fabric.Canvas(el.canvasEl, {
      preserveObjectStacking:true,
      selection:true,
      backgroundColor:'#ece6d6',
      enableRetinaScaling:false
    });

    const clamp=v=>Math.max(MIN_CM, Math.min(MAX_CM, Math.round(Number(v)||MIN_CM)));
    const calcPrice=(w,h)=>Math.round((100 + 0.125*w*h)*100)/100;
    const orderQuantity=()=>Math.max(1, Math.floor(Number(el.quantity?.value)||1));

    function viewMax() {
      const stage = root.querySelector('.bmc-stage-card');
      const available = stage
        ? Math.max(220, stage.getBoundingClientRect().width - 110)
        : Math.max(220, window.innerWidth - 110);
      if (window.innerWidth <= 520) return Math.min(available, 390);
      if (window.innerWidth <= 900) return Math.min(available, 520);
      return Math.min(available, 640);
    }

    function dims() {
      const w=clamp(el.width.value);
      const h=clamp(el.height.value);
      return {w,h};
    }

    function resize(source, preserveObjects=true) {
      const {w,h}=dims(source);
      el.width.value=w;
      el.height.value=h;

      const oldW = canvas.getWidth() || 1;
      const oldH = canvas.getHeight() || 1;
      const maxView = viewMax();
      const scale=Math.min(maxView/w, maxView/h, 8);
      const newW=Math.max(80, Math.round(w*scale));
      const newH=Math.max(80, Math.round(h*scale));

      if (preserveObjects && canvas.getObjects().length) {
        const sx = newW/oldW, sy = newH/oldH;
        canvas.getObjects().forEach(o=>{
          o.set({
            left:(o.left||0)*sx,
            top:(o.top||0)*sy,
            scaleX:(o.scaleX||1)*sx,
            scaleY:(o.scaleY||1)*sy
          });
          o.setCoords();
        });
      }

      canvas.setDimensions({width:newW,height:newH});
      canvas.calcOffset();
      canvas.requestRenderAll();

      el.canvasBorder.style.setProperty('--stitch-px', Math.max(1, STITCH_CM*scale)+'px');
      el.canvasBorder.style.setProperty('--safe-margin-px', Math.max(2, SAFE_MARGIN_CM*scale)+'px');
      el.rx.textContent=w+' cm';
      el.ry.textContent=h+' cm';
      const unitPrice=calcPrice(w,h);
      el.price.textContent='฿'+unitPrice.toFixed(2);

      return {w,h,scale};
    }

    el.backgroundColor.addEventListener('input',()=>{
      const color = el.backgroundColor.value || '#ece6d6';
      canvas.setBackgroundColor(color, canvas.requestRenderAll.bind(canvas));
      el.canvasBorder.style.backgroundColor = color;
    });

    ['width','height'].forEach(id=>el[id].addEventListener('change',()=>resize()));
    el.quantity?.addEventListener('input',()=>{
      if(Number(el.quantity.value)<1) el.quantity.value=1;
      resize(undefined,true);
    });
    window.addEventListener('resize',()=>resize(undefined,true), {passive:true});

    if ('ResizeObserver' in window) {
      const stage = root.querySelector('.bmc-stage-card');
      if(stage){
        let resizeTimer;
        const ro = new ResizeObserver(()=>{
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(()=>resize(undefined,true), 80);
        });
        ro.observe(stage);
      }
    }

    el.files.addEventListener('change',e=>{
      [...e.target.files].forEach(file=>{
        const reader=new FileReader();
        reader.onload=ev=>fabric.Image.fromURL(ev.target.result,img=>{
          img.set({
            left:canvas.width/2,
            top:canvas.height/2,
            originX:'center',
            originY:'center',
            name:file.name
          });
          const maxImageW = Math.max(40, canvas.width * 0.55);
          const maxImageH = Math.max(40, canvas.height * 0.55);
          img.scale(Math.min(maxImageW/img.width, maxImageH/img.height, 1));
          img.setCoords();
          canvas.add(img).setActiveObject(img);
          canvas.requestRenderAll();
          refreshLayers();
        });
        reader.readAsDataURL(file);
      });
      e.target.value='';
    });

    el.addText.addEventListener('click',()=>{
      const value=el.text.value.trim();
      if(!value)return;
      const t=new fabric.Textbox(value,{
        left:canvas.width/2,
        top:canvas.height/2,
        originX:'center',
        originY:'center',
        width:Math.max(100,canvas.width*.55),
        fontSize:Number(el.font.value)||42,
        fontFamily:el.fontFamily.value,
        fill:el.textColor.value,
        textAlign:'center',
        name:value.split('\n')[0].slice(0,24)
      });
      canvas.add(t).setActiveObject(t);
      refreshLayers();
    });

    const active=()=>canvas.getActiveObject();

    function scaleActive(factor){
      const obj = active();
      if(!obj) return;

      const nextX = Math.max(0.05, Math.min(12, (obj.scaleX || 1) * factor));
      const nextY = Math.max(0.05, Math.min(12, (obj.scaleY || 1) * factor));

      obj.set({
        scaleX: nextX,
        scaleY: nextY
      });
      obj.setCoords();
      keepObjectInsideCanvas(obj);
      canvas.setActiveObject(obj);
      canvas.requestRenderAll();
      refreshLayers();
    }

    el.scaleDown.onclick=()=>scaleActive(0.85);
    el.scaleUp.onclick=()=>scaleActive(1.15);

    function keepObjectInsideCanvas(obj){
      if(!obj) return;
      obj.setCoords();

      const rect = obj.getBoundingRect(true, true);
      const cw = canvas.getWidth();
      const ch = canvas.getHeight();
      let dx = 0, dy = 0;

      if(rect.width <= cw){
        if(rect.left < 0) dx = -rect.left;
        else if(rect.left + rect.width > cw) dx = cw - (rect.left + rect.width);
      }else{
        dx = (cw / 2) - (rect.left + rect.width / 2);
      }

      if(rect.height <= ch){
        if(rect.top < 0) dy = -rect.top;
        else if(rect.top + rect.height > ch) dy = ch - (rect.top + rect.height);
      }else{
        dy = (ch / 2) - (rect.top + rect.height / 2);
      }

      if(dx || dy){
        obj.set({
          left:(obj.left||0) + dx,
          top:(obj.top||0) + dy
        });
        obj.setCoords();
      }
    }

    canvas.on('object:modified', e=>{
      keepObjectInsideCanvas(e.target);
      canvas.requestRenderAll();
    });

    canvas.on('object:moving', e=>{
      const obj = e.target;
      if(!obj) return;
      const margin = 24;
      const rect = obj.getBoundingRect(true, true);
      const cw = canvas.getWidth(), ch = canvas.getHeight();

      if(rect.left + rect.width < margin) obj.left += margin - (rect.left + rect.width);
      if(rect.left > cw - margin) obj.left -= rect.left - (cw - margin);
      if(rect.top + rect.height < margin) obj.top += margin - (rect.top + rect.height);
      if(rect.top > ch - margin) obj.top -= rect.top - (ch - margin);
    });

    el.layerUp.onclick=()=>{
      const obj=active();
      if(obj){canvas.bringForward(obj);canvas.setActiveObject(obj);canvas.requestRenderAll();refreshLayers();}
    };
    el.layerDown.onclick=()=>{
      const obj=active();
      if(obj){canvas.sendBackwards(obj);canvas.setActiveObject(obj);canvas.requestRenderAll();refreshLayers();}
    };
    el.del.onclick=()=>{
      const obj=active();
      if(obj){canvas.remove(obj);canvas.discardActiveObject();canvas.requestRenderAll();refreshLayers();}
    };

    function isText(obj){ return obj && ['textbox','text','i-text'].includes(obj.type); }

    el.fontFamily.addEventListener('change',()=>{
      const obj=active(); if(isText(obj)){obj.set('fontFamily',el.fontFamily.value);canvas.requestRenderAll();}
    });
    el.textColor.addEventListener('input',()=>{
      const obj=active(); if(isText(obj)){obj.set('fill',el.textColor.value);canvas.requestRenderAll();}
    });
    el.font.addEventListener('change',()=>{
      const obj=active();
      if(isText(obj)){obj.set('fontSize',Math.max(8,Math.min(240,Number(el.font.value)||42)));canvas.requestRenderAll();}
    });

    function syncTextControls(){
      const obj=active();
      if(!isText(obj)) return;
      if([...el.fontFamily.options].some(o=>o.value===obj.fontFamily)) el.fontFamily.value=obj.fontFamily;
      if(/^#[0-9a-f]{6}$/i.test(obj.fill||'')) el.textColor.value=obj.fill;
      el.font.value=Math.round(obj.fontSize||42);
    }

    function refreshLayers(){
      const objects=canvas.getObjects(), a=active();
      el.layers.innerHTML='';
      [...objects].reverse().forEach(o=>{
        const d=document.createElement('button');
        d.type='button';
        d.className='bmc-layer'+(o===a?' is-active':'');
        d.textContent=isText(o)
          ? `Text: ${(o.text||'').replace(/\n/g,' / ').slice(0,34)}`
          : `Image: ${o.name||'layer'}`;
        d.onclick=()=>{
          canvas.setActiveObject(o);
          canvas.requestRenderAll();
          syncTextControls();
          refreshLayers();
        };
        el.layers.appendChild(d);
      });
      if(!objects.length) el.layers.innerHTML='<div class="bmc-empty">No layers yet</div>';
    }

    ['selection:created','selection:updated'].forEach(evt=>canvas.on(evt,()=>{syncTextControls();refreshLayers();}));
    ['selection:cleared','object:modified','object:added','object:removed'].forEach(evt=>canvas.on(evt,refreshLayers));

    // --- PNG metadata helper: insert pHYs chunk so print software knows physical size/DPI.
    const crcTable = (() => {
      let c, table=[];
      for(let n=0;n<256;n++){
        c=n;
        for(let k=0;k<8;k++) c=((c&1)?(0xedb88320^(c>>>1)):(c>>>1));
        table[n]=c>>>0;
      }
      return table;
    })();

    function crc32(bytes){
      let c=0xffffffff;
      for(let i=0;i<bytes.length;i++) c=crcTable[(c^bytes[i])&0xff]^(c>>>8);
      return (c^0xffffffff)>>>0;
    }

    function u32be(n){
      return new Uint8Array([(n>>>24)&255,(n>>>16)&255,(n>>>8)&255,n&255]);
    }

    function concatArrays(arrays){
      const len=arrays.reduce((s,a)=>s+a.length,0), out=new Uint8Array(len);
      let off=0; arrays.forEach(a=>{out.set(a,off);off+=a.length;});
      return out;
    }

    async function addPngDpi(blob,dpi){
      const src=new Uint8Array(await blob.arrayBuffer());
      const sig=src.slice(0,8);
      let pos=8, parts=[sig], inserted=false;
      const ppm=Math.round(dpi/0.0254);

      while(pos<src.length){
        const len=(src[pos]<<24)|(src[pos+1]<<16)|(src[pos+2]<<8)|src[pos+3];
        const chunk=src.slice(pos,pos+12+len);
        const type=String.fromCharCode(...src.slice(pos+4,pos+8));
        parts.push(chunk);
        pos+=12+len;

        if(type==='IHDR' && !inserted){
          const typeBytes=new TextEncoder().encode('pHYs');
          const data=concatArrays([u32be(ppm),u32be(ppm),new Uint8Array([1])]);
          const crc=u32be(crc32(concatArrays([typeBytes,data])));
          parts.push(concatArrays([u32be(data.length),typeBytes,data,crc]));
          inserted=true;
        }
      }
      return new Blob(parts,{type:'image/png'});
    }


    async function createProductionPng(){
      const {w,h}=dims();
      const pxW=Math.round((w/2.54)*EXPORT_DPI);
      const pxH=Math.round((h/2.54)*EXPORT_DPI);
      const pixelCount=pxW*pxH;

      if(pixelCount > 65_000_000){
        throw new Error(`This design is very large (${pxW} × ${pxH} px). Please use a desktop browser or reduce the mat size.`);
      }

      canvas.discardActiveObject();
      canvas.requestRenderAll();

      const json=canvas.toJSON(['name']);
      const exportCanvasEl=document.createElement('canvas');
      exportCanvasEl.width=pxW;
      exportCanvasEl.height=pxH;

      const exportFabric=new fabric.StaticCanvas(exportCanvasEl,{
        backgroundColor:el.backgroundColor.value || '#ece6d6'
      });

      await new Promise((resolve)=>{
        exportFabric.loadFromJSON(json,()=>{
          const sx=pxW/canvas.getWidth();
          const sy=pxH/canvas.getHeight();

          exportFabric.getObjects().forEach(o=>{
            o.set({
              left:(o.left||0)*sx,
              top:(o.top||0)*sy,
              scaleX:(o.scaleX||1)*sx,
              scaleY:(o.scaleY||1)*sy
            });
            o.setCoords();
          });

          exportFabric.renderAll();
          resolve();
        });
      });

      const rawBlob=await new Promise((resolve,reject)=>{
        exportCanvasEl.toBlob(
          b=>b?resolve(b):reject(new Error('Could not create the production PNG.')),
          'image/png'
        );
      });

      const blob=await addPngDpi(rawBlob,EXPORT_DPI);
      exportFabric.dispose();

      return {
        blob,
        widthCm:w,
        heightCm:h,
        pxW,
        pxH
      };
    }

    function attachBlobAsFile(blob, filename){
      if(!el.designFileHolder) throw new Error('Design file holder is missing.');

      el.designFileHolder.innerHTML='';

      const fileInput=document.createElement('input');
      fileInput.type='file';
      fileInput.name='properties[Production design]';
      fileInput.hidden=true;

      const file=new File([blob],filename,{type:'image/png'});
      const transfer=new DataTransfer();
      transfer.items.add(file);
      fileInput.files=transfer.files;

      el.designFileHolder.appendChild(fileInput);
    }

    el.addCart?.addEventListener('click', async ()=>{
      if(!el.shopifyForm){
        el.status.textContent='Shopify order form is not available on this page.';
        return;
      }

      const qty=orderQuantity();
      const {w,h}=dims();
      const unitPrice=calcPrice(w,h);

      el.addCart.disabled=true;
      el.addCart.textContent='PREPARING DESIGN…';
      el.status.hidden=false;
      el.status.textContent='Preparing design…';

      try{
        const production=await createProductionPng();

        attachBlobAsFile(
          production.blob,
          `bar-mat-${w}x${h}cm-${EXPORT_DPI}dpi.png`
        );

        el.formQuantity.value=String(qty);
        el.propWidth.value=String(w);
        el.propHeight.value=String(h);
        el.propBackground.value=el.backgroundColor.value || '#ece6d6';
        el.propPrice.value='฿'+(unitPrice*qty).toFixed(2);

        el.status.hidden=false;
        el.status.textContent='Adding to cart…';
        el.shopifyForm.submit();
      }catch(err){
        console.error(err);
        el.status.hidden=false;
        el.status.textContent=err.message || 'Could not prepare the design for Shopify.';
        el.addCart.disabled=false;
        el.addCart.textContent='ADD TO CART';
      }
    });

    resize(undefined,false);
    refreshLayers();
  }

  function boot() {
    if (!window.fabric) return setTimeout(boot, 60);
    document.querySelectorAll('[data-barmat-customizer]').forEach(initBarmat);
  }

  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',boot);
  else boot();

  document.addEventListener('shopify:section:load', e=>{
    if(!window.fabric) return;
    e.target.querySelectorAll?.('[data-barmat-customizer]').forEach(initBarmat);
  });
})();
